import React, { useState } from "react";
import { X, Loader2, CheckCircle, Smartphone } from "lucide-react";

const PhoneAuthModal = ({ onClose, onSuccess }) => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(process.env.REACT_APP_API_BASE + "/api/code", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `phone=${encodeURIComponent(phone)}`,
      });

      const data = await response.json();

      if (response.ok) {
        setCodeSent(true);
        setSuccess(true);
      } else {
        setError(data.detail || "인증번호 전송에 실패했습니다.");
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(process.env.REACT_APP_API_BASE + "/api/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `phone=${encodeURIComponent(phone)}&code=${encodeURIComponent(code)}`,
      });

      const data = await response.json();

      if (response.ok && data.token) {
        localStorage.setItem("auth_token", data.token);

        // Fetch user information
        try {
          const userResponse = await fetch(process.env.REACT_APP_API_BASE + "/api/me", {
            method: "GET",
            headers: {
              Authorization: data.token,
            },
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            // Call success callback with user data
            onSuccess(userData);
          } else {
            // If user info fetch fails, still close modal but don't pass user data
            onSuccess();
          }
        } catch (err) {
          // If user info fetch fails, still close modal but don't pass user data
          onSuccess();
        }
      } else {
        setError(data.detail || "인증에 실패했습니다.");
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <Smartphone className="w-5 h-5 text-teal-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-800">휴대폰 인증으로 로그인</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={codeSent ? handleCodeSubmit : handlePhoneSubmit}>
          {/* Phone Number Input */}
          <div className="mb-3">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              휴대폰 번호
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2">
                <input type="tel" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01012345678" className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" required disabled={codeSent} />
              </div>
              <div>
                <button type="submit" disabled={loading || !phone || codeSent} className="w-full h-10 bg-teal-500 text-white px-3 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1 transition-colors text-sm whitespace-nowrap">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>전송 중</span>
                    </>
                  ) : (
                    <span>인증번호 전송</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Verification Code Input */}
          <div className="mb-3">
            <div className="flex items-center mb-1">
              <CheckCircle className={`w-4 h-4 ${codeSent ? "text-green-500" : "text-gray-300"} mr-1`} />
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                인증번호 입력
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-2">{codeSent ? `${phone}로 전송된 6자리 인증번호를 입력해주세요.` : "휴대폰 번호를 입력하고 인증번호를 전송해주세요."}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2">
                <input type="text" id="code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={`w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${!codeSent ? "bg-gray-100" : ""}`} required disabled={!codeSent} />
              </div>
              <div>
                <button type="submit" disabled={loading || !code || !codeSent} className="w-full h-10 bg-teal-500 text-white px-3 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1 transition-colors text-sm whitespace-nowrap">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>확인 중</span>
                    </>
                  ) : (
                    <span>인증하기</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {error && <div className="mt-3 p-2 bg-red-50 rounded-lg text-red-700 border border-red-200 text-sm">{error}</div>}

        {success && codeSent && <div className="mt-3 p-2 bg-green-50 rounded-lg text-green-700 border border-green-200 text-sm">인증번호가 전송되었습니다.</div>}

        <p className="mt-4 text-xs text-gray-500 text-center">
          인증을 마치고 로그인하여{" "}
          <a href="//layable.kr/#/tos" className="text-teal-600 hover:text-teal-800">
            이용약관
          </a>
          과{" "}
          <a href="//layable.kr/#/privacy" className="text-teal-600 hover:text-teal-800">
            개인정보처리방침
          </a>
          에 동의합니다.
        </p>
      </div>
    </div>
  );
};

export default PhoneAuthModal;
